import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import 'semantic-ui-css/semantic.min.css';
import { Table, Input, Dropdown } from 'semantic-ui-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/style/style.scss';
import './assets/img/logo_front.png';

const seasonOptions = [
  { key: '2025', text: 'Saison 2025/2026', value: '2025' },
  { key: '2024', text: 'Saison 2024/2025', value: '2024' },
  { key: '2023', text: 'Saison 2023/2024', value: '2023' },
];

const genderOptions = [
  { key: 'male', text: 'Homme', value: 'male' },
  { key: 'female', text: 'Femme', value: 'female' },
];

const searchModeOptions = [
  { key: 'club', text: 'Rechercher un club', value: 'club' },
  { key: 'person', text: 'Rechercher une personne', value: 'person' },
];

const getCsvUrl = (season, gender) => {
  const baseUrls = {
    '2025': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQo5-YqMdciz9mB0xICnp1_f3oyodh3Rdoykyicjfd2qlIn8fzUT9vgWNl-ERBqiyLb80Rgg6mZwKaR/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQo5-YqMdciz9mB0xICnp1_f3oyodh3Rdoykyicjfd2qlIn8fzUT9vgWNl-ERBqiyLb80Rgg6mZwKaR/pub?output=csv',
    },
    '2024': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vR0w0PDulvh5c6zLSRfKhq-0JATaIAnFVIYkizbWefHfIotzRV0x5Stm2juI6_91LdM2_MAhAYbY5ge/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vR0w0PDulvh5c6zLSRfKhq-0JATaIAnFVIYkizbWefHfIotzRV0x5Stm2juI6_91LdM2_MAhAYbY5ge/pub?output=csv',
    },
    '2023': {
      male: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSj31GOuRQ2tTJ_uSQKj2L0Y5cqRceS1fw4GiHOoXVVS01RQs0MGHFqGo-EegCewKHs21ukjmiEEvdj/pub?gid=1290048305&output=csv',
      female: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vSj31GOuRQ2tTJ_uSQKj2L0Y5cqRceS1fw4GiHOoXVVS01RQs0MGHFqGo-EegCewKHs21ukjmiEEvdj/pub?output=csv',
    },
  };
  return baseUrls[season]?.[gender] || baseUrls['2025']['male'];
};

const App = () => {
  const [season, setSeason] = useState('2025');
  const [gender, setGender] = useState('female');
  const [data, setData] = useState([]);

  const [filteredClubs, setFilteredClubs] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [searchMode, setSearchMode] = useState('club');

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [membersPerPage] = useState(10);

  const clubResultsRef = useRef(null);
  const personResultsRef = useRef(null);

  // ====================== FETCH CSV ======================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const csvUrl = getCsvUrl(season, gender);
        const response = await axios.get(csvUrl);

        const parsed = Papa.parse(response.data, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
          transformHeader: (header) => header.trim(),
          transform: (value) => (value ? value.toString().trim() : ''),
        });

        const result = parsed.data;

        console.log("=== NOMS DES COLONNES ===");
        console.table(parsed.meta.fields);
        if (result.length > 0) console.log("Première ligne :", result[0]);

        setData(result);
      } catch (err) {
        console.error("Erreur lors du chargement du CSV :", err);
      }
    };

    fetchData();
  }, [season, gender]);

  // Scroll automatique
  useEffect(() => {
    if (selectedClub && clubResultsRef.current) {
      clubResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedClub]);

  useEffect(() => {
    if (selectedPerson && personResultsRef.current) {
      personResultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedPerson]);

  const handleSearchChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    setFilteredClubs([]);
    setFilteredMembers([]);
    setSelectedClub(null);
    setSelectedPerson(null);

    if (!term) return;

    if (searchMode === 'club') {
      const clubs = [...new Set(
        data.filter(item => item['Club']?.toLowerCase().includes(term))
          .map(item => item['Club'])
      )];
      setFilteredClubs(clubs);
    } else {
      const members = data.filter(item =>
        item['Prénom']?.toLowerCase().includes(term) ||
        item['Nom']?.toLowerCase().includes(term)
      );
      setFilteredMembers(members);
    }
  };

  const handleSearchModeChange = (e, { value }) => {
    setSearchMode(value);
    setSearchTerm('');
    setFilteredClubs([]);
    setFilteredMembers([]);
    setSelectedClub(null);
    setSelectedPerson(null);
  };

  const handleClubSelect = (club) => {
    setSelectedClub(club);
    setSelectedPerson(null);
    setFilteredClubs([]);
    setSearchTerm(club);
  };

  const handlePersonSelect = (person) => {
    setSelectedPerson(person);
    setSelectedClub(null);
    setFilteredMembers([]);
    setSearchTerm(`${person['Nom']} ${person['Prénom']}`);
  };

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    setIsModalOpen(true);
  };

  const getCategoryMembers = (cat) =>
    data.filter(item => item['Catégorie'] === cat);

  const selectedClubMembers = data.filter(item => item['Club'] === selectedClub);

  const lastRank = data.length;

  const getCategoryRanking = (members) => {
    const total = members.length;
    return members.map((m, i) => ({
      ...m,
      CategoryRank: `${i + 1} sur ${total}`,
    }));
  };

  const paginatedMembers = (members) => {
    const start = (currentPage - 1) * membersPerPage;
    return members.slice(start, start + membersPerPage);
  };

  const totalPages = selectedCategory
    ? Math.ceil(getCategoryMembers(selectedCategory).length / membersPerPage)
    : 1;

  return (
    <div className="container-fluid">

      {/* === HEADER === */}
      <div className="premium-header text-center py-5">
        <div className="container">
          <div className="mb-4">
            <img
              src={require('./assets/img/logo_front.png')}
              alt="French Ranking Logo"
              className="premium-logo"
            />
          </div>

          <h1 className="premium-title mb-5">Recherche d'un patineur ou un club</h1>

          <div className="col-12 col-lg-6 mx-auto">
            <Dropdown fluid selection options={seasonOptions} value={season} onChange={(e, { value }) => setSeason(value)} className="premium-dropdown mb-3" />
            <Dropdown fluid selection options={genderOptions} value={gender} onChange={(e, { value }) => setGender(value)} className="premium-dropdown mb-3" />
            <Dropdown fluid selection options={searchModeOptions} value={searchMode} onChange={handleSearchModeChange} className="premium-dropdown mb-3" />

            <div className="search-wrapper mx-auto" style={{ maxWidth: '600px' }}>
              <Input
                icon="search"
                fluid
                placeholder={searchMode === 'club' ? 'Rechercher un club...' : 'Rechercher une personne...'}
                value={searchTerm}
                onChange={handleSearchChange}
                className="premium-search-input"
              />

              {(filteredClubs.length > 0 || filteredMembers.length > 0) && searchTerm && (
                <div className="search-results">
                  {searchMode === 'club' &&
                    filteredClubs.map((club, i) => (
                      <div key={i} className="result-item" onClick={() => handleClubSelect(club)}>
                        🏆 {club}
                      </div>
                    ))}

                  {searchMode === 'person' &&
                    filteredMembers.map((person, i) => (
                      <div key={i} className="result-item" onClick={() => handlePersonSelect(person)}>
                        👤 {person['Nom']} {person['Prénom']} — {person['Club']}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* === RÉSULTATS CLUB === */}
      {selectedClub && (
        <div ref={clubResultsRef} className="container mt-5">
          <h3 className="text-center mb-4 premium-section-title">
            Membres du club : <span className="text-primary">{selectedClub}</span>
          </h3>
          <Table celled compact className="premium-table">
            <Table.Header>
              <Table.Row className="text-center">
                <Table.HeaderCell>Nom Prénom</Table.HeaderCell>
                <Table.HeaderCell>Naissance</Table.HeaderCell>
                <Table.HeaderCell>Classement Général</Table.HeaderCell>
                <Table.HeaderCell>Médaille</Table.HeaderCell>
                <Table.HeaderCell>Catégorie</Table.HeaderCell>
                <Table.HeaderCell>Meilleur Score</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {selectedClubMembers.map((member, index) => (
                <Table.Row key={index} className="text-center">
                  <Table.Cell>{member['Nom']} {member['Prénom']}</Table.Cell>
                  <Table.Cell>{member['Naissance']}</Table.Cell>
                  <Table.Cell>{member['Cl. Gal']} ème sur {lastRank}</Table.Cell>
                  <Table.Cell>{member['Méd.']}</Table.Cell>
                  <Table.Cell
                    className="text-danger fw-bold"
                    onClick={() => handleCategoryClick(member['Catégorie'])}
                    style={{ cursor: 'pointer' }}
                  >
                    {member['Catégorie']}
                  </Table.Cell>
                  <Table.Cell>{member['Season Best'] || member['Score Best'] || '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* === RÉSULTATS PERSONNE === */}
      {selectedPerson && (
        <div ref={personResultsRef} className="container mt-5">
          <h3 className="text-center mb-4 premium-section-title">
            Détails de : <span className="text-primary">{selectedPerson['Nom']} {selectedPerson['Prénom']}</span>
          </h3>
          <Table celled compact className="premium-table mb-5">
            <Table.Body>
              <Table.Row>
                <Table.Cell><strong>Nom complet</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Nom']} {selectedPerson['Prénom']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Date de naissance</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Naissance']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Club</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Club']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Classement Général</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Cl. Gal']} ème sur {lastRank}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Médaille</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Méd.']}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Catégorie</strong></Table.Cell>
                <Table.Cell
                  className="text-danger fw-bold"
                  onClick={() => handleCategoryClick(selectedPerson['Catégorie'])}
                  style={{ cursor: 'pointer' }}
                >
                  {selectedPerson['Catégorie']}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell><strong>Meilleur Score</strong></Table.Cell>
                <Table.Cell>{selectedPerson['Season Best'] || selectedPerson['Score Best'] || '—'}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      )}

      {/* ====================== MODALE BOOTSTRAP ====================== */}
      {isModalOpen && selectedCategory && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '1150px' }}>
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">
                  Membres de la catégorie : <strong>{selectedCategory}</strong>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                <table className="table table-bordered table-hover text-center align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom Prénom</th>
                      <th>Naissance</th>
                      <th>Place dans la catégorie</th>
                      <th>Classement général</th>
                      <th>Médaille</th>
                      <th>Club</th>
                      <th>Best Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMembers(getCategoryRanking(getCategoryMembers(selectedCategory))).map((m, i) => (
                      <tr key={i}>
                        <td>{m['Nom']} {m['Prénom']}</td>
                        <td>{m['Naissance']}</td>
                        <td><strong>{m.CategoryRank}</strong></td>
                        <td>{m['Cl. Gal']} ème sur {lastRank}</td>
                        <td>{m['Méd.']}</td>
                        <td>{m['Club']}</td>
                        <td>{m['Season Best'] || m['Score Best'] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                    <span className="text-muted">
                      Page <strong>{currentPage}</strong> sur {totalPages}
                    </span>

                    <div className="btn-group">
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Précédent
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Fermer
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;